import moment from 'moment';

const PLACEHOLDER_VALUES = new Set(['Add Time', 'Invalid date']);

/** Message when both times parse and end is not strictly after start. */
export const TIME_RANGE_ORDER_ERROR =
    'End time must be later than start time.';

function isIncompleteTime(value: unknown): boolean {
    if (value == null || value === '') {
        return true;
    }
    if (typeof value === 'string') {
        const t = value.trim();
        if (!t || PLACEHOLDER_VALUES.has(t)) {
            return true;
        }
    }
    return false;
}

/** Seconds since midnight (local) for ordering same-day times. */
function parseToSecondsSinceMidnight(value: string): number | null {
    const trimmed = value.trim();
    const strictFormats = ['HH:mm:ss', 'HH:mm', 'h:mm A'];
    for (const fmt of strictFormats) {
        const m = moment(trimmed, fmt, true);
        if (m.isValid()) {
            return (
                m.hour() * 3600 +
                m.minute() * 60 +
                m.second() +
                m.millisecond() / 1000
            );
        }
    }
    const iso = moment(trimmed, moment.ISO_8601, true);
    if (iso.isValid()) {
        return (
            iso.hour() * 3600 +
            iso.minute() * 60 +
            iso.second() +
            iso.millisecond() / 1000
        );
    }
    const loose = moment(trimmed);
    if (!loose.isValid()) {
        return null;
    }
    return (
        loose.hour() * 3600 +
        loose.minute() * 60 +
        loose.second() +
        loose.millisecond() / 1000
    );
}

/**
 * Returns an error message if end time is not strictly after start time.
 * Returns null if validation is skipped (incomplete/placeholder) or order is valid.
 */
export function getTimeRangeValidationMessage(
    start: unknown,
    end: unknown
): string | null {
    if (isIncompleteTime(start) || isIncompleteTime(end)) {
        return null;
    }
    const startStr = String(start).trim();
    const endStr = String(end).trim();
    const startSec = parseToSecondsSinceMidnight(startStr);
    const endSec = parseToSecondsSinceMidnight(endStr);
    if (startSec == null || endSec == null) {
        return null;
    }
    if (endSec <= startSec) {
        return TIME_RANGE_ORDER_ERROR;
    }
    return null;
}

/** Shown when one side is filled but the other is still a placeholder (e.g. only start time picked). */
export const BOTH_TIMES_REQUIRED = 'Please select both start and end time.';

/**
 * Validates completeness first (both sides non-placeholder), then order (end after start).
 * Returns null when both sides are still incomplete (nothing to validate yet).
 */
export function getTimeRangeValidationWithCompleteness(
    start: unknown,
    end: unknown
): string | null {
    const incIn = isIncompleteTime(start);
    const incOut = isIncompleteTime(end);
    if (incIn && incOut) {
        return null;
    }
    if (incIn || incOut) {
        return BOTH_TIMES_REQUIRED;
    }
    return getTimeRangeValidationMessage(start, end);
}

/** True only when both values are present and not placeholders (e.g. not "Add Time"). */
export function hasCompleteTimePair(start: unknown, end: unknown): boolean {
    return !isIncompleteTime(start) && !isIncompleteTime(end);
}

/** Rosters / save-bar payload shapes: nested or flat totalMissed. */
export function getInvalidTimeRangeFromPayload(payload: unknown): string | null {
    if (payload == null || typeof payload !== 'object') {
        return null;
    }
    const p = payload as Record<string, unknown>;
    const tryPair = (checkIn: unknown, checkOut: unknown): string | null =>
        getTimeRangeValidationWithCompleteness(checkIn, checkOut);

    /** Roster save-bar passes flat `added*Info` rows with checkInTime / checkOutTime only. */
    const flatMissMsg = tryPair(p['checkInTime'], p['checkOutTime']);
    if (flatMissMsg) {
        return flatMissMsg;
    }

    const ftRoot = p['facilitationTime'] as unknown[] | undefined;
    if (ftRoot?.[0] && typeof ftRoot[0] === 'object') {
        const row = ftRoot[0] as Record<string, unknown>;
        const m = tryPair(row['checkInTime'], row['checkOutTime']);
        if (m) {
            return m;
        }
    }
    const facilitation = p['facilitation'] as Record<string, unknown> | undefined;
    const ftNested = facilitation?.['facilitationTime'] as unknown[] | undefined;
    if (ftNested?.[0] && typeof ftNested[0] === 'object') {
        const row = ftNested[0] as Record<string, unknown>;
        const m = tryPair(row['checkInTime'], row['checkOutTime']);
        if (m) {
            return m;
        }
    }
    const ptRoot = p['participationTime'] as unknown[] | undefined;
    if (ptRoot?.[0] && typeof ptRoot[0] === 'object') {
        const row = ptRoot[0] as Record<string, unknown>;
        const m = tryPair(row['checkInTime'], row['checkOutTime']);
        if (m) {
            return m;
        }
    }
    const participation = p['participation'] as Record<string, unknown> | undefined;
    const ptNested = participation?.['participationTime'] as unknown[] | undefined;
    if (ptNested?.[0] && typeof ptNested[0] === 'object') {
        const row = ptNested[0] as Record<string, unknown>;
        const m = tryPair(row['checkInTime'], row['checkOutTime']);
        if (m) {
            return m;
        }
    }
    const tmRoot = p['totalMissed'] as unknown[] | undefined;
    if (tmRoot?.[0] && typeof tmRoot[0] === 'object') {
        const row = tmRoot[0] as Record<string, unknown>;
        const m = tryPair(row['checkInTime'], row['checkOutTime']);
        if (m) {
            return m;
        }
    }
    const miss = p['miss'] as Record<string, unknown> | undefined;
    if (miss) {
        const tm = miss['totalMissed'] as unknown[] | undefined;
        if (tm?.[0] && typeof tm[0] === 'object') {
            const row = tm[0] as Record<string, unknown>;
            const m = tryPair(row['checkInTime'], row['checkOutTime']);
            if (m) {
                return m;
            }
        }
    }
    return null;
}
